import { useCallback, useMemo } from 'react';

import { useScenariosList } from '@/entities/scenarios';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { useSjActions } from '@/shared/lib/sj-orm/hooks/useSjActions';
import { AssetDto } from '@/shared/lib/sj-orm/models/asset.dto';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { AIStatus } from '@/shared/types';

export const useAssets = () => {
  const store = useSJDatabase();
  const sjAssets = useSjActions('assets');
  const { loading: scenariosLoading, scenarios } = useScenariosList();

  const getAssetsList = useCallback(
    (includeTest?: boolean) => {
      if (!sjAssets.isDbReady) return []; // TODO remove. Add loader to pages (PageAssets)

      return includeTest
        ? sjAssets.getAll()
        : sjAssets.findMany((item) => item.isTest === undefined || !item.isTest);
    },
    [sjAssets],
  );

  const getAssetInformation = useCallback(
    (id: string) => {
      return store.collections.assetInformationDocument?.findById(id);
    },
    [store],
  );

  const getAssetContacts = useCallback(
    (id: string) => {
      return store.collections.contacts?.findMany((contact) => contact.assetId === id);
    },
    [store],
  );

  const getAssetPrivateDocuments = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (id: string) => {
      const privateDocumentsIds: string[] = [];
      const result = new Set();
      const asset = store.collections.assets?.findOne((item) => item.id === id);

      if (asset) {
        Object.values(asset).forEach((item) => {
          if (typeof item === 'string' && item.startsWith('privateDocuments-')) {
            privateDocumentsIds.push(item);
          } else if (Array.isArray(item)) {
            item.forEach((as) => {
              if (typeof as === 'string' && as?.startsWith('privateDocuments-')) {
                privateDocumentsIds.push(as);
              }
            });
          }
        });

        if (privateDocumentsIds.length) {
          privateDocumentsIds.forEach((documentId) => {
            const privateDocument = store.collections.privateDocuments?.findOne((document) => {
              return document.id === documentId;
            });
            if (privateDocument) result.add(privateDocument);
          });
        }
        return result;
      } else {
        return undefined;
      }
    },
    [store],
  );

  const getAssetPersonalIdentifiers = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (id: string) => {
      const personalIdentifiersDocumentsIds: string[] = [];
      const result = new Set();
      const asset = store.collections.assets?.findOne((item) => item.id === id);

      if (asset) {
        Object.values(asset).forEach((item) => {
          if (typeof item === 'string' && item.startsWith('personalIdentifiersDocuments-')) {
            personalIdentifiersDocumentsIds.push(item);
          } else if (Array.isArray(item)) {
            item.forEach((as) => {
              if (typeof as === 'string' && as.startsWith('personalIdentifiersDocuments-')) {
                personalIdentifiersDocumentsIds.push(as);
              }
            });
          }
        });

        if (personalIdentifiersDocumentsIds.length) {
          personalIdentifiersDocumentsIds.forEach((documentId) => {
            const personalIdentifier = store.collections.personalIdentifiersDocuments?.findOne(
              (document) => {
                return document.id === documentId;
              },
            );
            if (personalIdentifier) result.add(personalIdentifier);
          });
        }
        return result;
      } else {
        return undefined;
      }
    },
    [store],
  );

  const deleteAsset = useCallback(
    (id: string) => {
      store.collections.assets?.remove({ id } as AssetDto);
    },
    [store],
  );

  const deleteAssets = useCallback(
    (ids: Array<string>) => {
      store.collections.assets?.removeMany((asset) => ids.includes(asset.id));
    },
    [store],
  );

  const verifyAiAsset = useCallback(async (assetId: string) => {
    const assetData = store.collections.assets?.findById(assetId);
    if (!assetData) return;

    await store.collections.assets?.update({
      ...assetData,
      aiStatus: AIStatus.VERIFIED,
    });
  }, []);

  const isAssetWithScenario = useCallback(
    (assetId: string) => {
      return !!scenarios?.some((s) => s.dtoIds?.includes(assetId));
    },
    [scenarios],
  );

  const isAssetWithBeneficiary = useCallback(
    (assetId: string, beneficiaries: number[]) => {
      scenarios?.some(
        (scenario) =>
          scenario.transfers.some((transfer) =>
            beneficiaries.includes(transfer.recipientProfileId),
          ) && scenario.dtoIds.includes(assetId),
      );
    },
    [scenarios],
  );

  const allBeneficiariesWithScenarios = useMemo(() => {
    if (!scenarios) {
      return [];
    }

    const beneficiariesSet = scenarios.reduce<Set<number>>((acc, scenario) => {
      scenario.transfers?.forEach((item) => acc.add(item.recipientProfileId));
      return acc;
    }, new Set());

    return Array.from(beneficiariesSet);
  }, [scenarios]);

  const allCoOwners = useMemo(() => {
    const assets = store.collections.assets?.findMany(Boolean);

    if (!assets) return [];

    let coOwners: Array<string> = [];

    for (const asset of assets) {
      if (Array.isArray(asset.ownerDocument)) {
        coOwners = coOwners.concat(asset.ownerDocument);
      }
      if (Array.isArray(asset.coOwners)) {
        for (const coOwner of asset.coOwners) {
          coOwners = coOwners.concat(coOwner.coOwnerDocument);
        }
      }
    }

    if (!coOwners.length) return [];

    return [...new Set(coOwners)];
  }, [store]);

  const isAssetWithOwners = useCallback(
    (assetId: string, owners: string[]) => {
      const asset = store.collections.assets?.findOne((a) => a.id === assetId);

      if (!asset) return false;

      let coOwners: Array<string> = [];

      if (Array.isArray(asset.ownerDocument)) {
        coOwners = coOwners.concat(asset.ownerDocument);
      }
      if (Array.isArray(asset.coOwners)) {
        for (const coOwner of asset.coOwners) {
          coOwners = coOwners.concat(coOwner.coOwnerDocument);
        }
      }

      return coOwners.some((co) => owners.includes(co));
    },
    [store],
  );

  const addAssetAttachments = useCallback(
    (asset: AssetDto, filesPaths: string[]) => {
      const attachments = asset.attachments ?? [];
      store.collections.assets?.update({ ...asset, attachments: [...attachments, ...filesPaths] });
    },
    [store],
  );

  const removeAssetAttachments = useCallback(
    (asset: AssetDto, ids: DtoId[]) => {
      const filesPaths =
        store.collections.fileMetaInfos
          ?.findMany((col) => ids.includes(col.id))
          .map((col) => col.filePath) ?? [];

      const attachments = asset.attachments?.filter((path) => !filesPaths.includes(path)) ?? [];
      store.collections.assets?.update({ ...asset, attachments });
    },
    [store],
  );

  return useMemo(
    () => ({
      getAssetsList,
      getAsset: sjAssets.findById,
      getAssetInformation,
      getAssetContacts,
      getAssetPrivateDocuments,
      getAssetPersonalIdentifiers,
      deleteAsset,
      deleteAssets,
      verifyAiAsset,
      removeAssetAttachments,
      isAssetWithScenario,
      isAssetWithBeneficiary,
      allBeneficiariesWithScenarios,
      allCoOwners,
      isAssetWithOwners,
      loading: scenariosLoading,
      addAssetAttachments,
    }),
    [
      sjAssets,
      getAssetsList,
      getAssetInformation,
      getAssetContacts,
      getAssetPrivateDocuments,
      getAssetPersonalIdentifiers,
      deleteAsset,
      deleteAssets,
      verifyAiAsset,
      removeAssetAttachments,
      isAssetWithScenario,
      isAssetWithBeneficiary,
      allBeneficiariesWithScenarios,
      allCoOwners,
      isAssetWithOwners,
      scenariosLoading,
      addAssetAttachments,
    ],
  );
};
